import { Box, Button, Divider, FormControl, MenuItem, Select } from '@mui/material';

import { useT } from '@/hooks';

interface TradeTabsProps {
  activeTab: 'buy' | 'sell';
  orderType: 'market' | 'limit' | 'more';
  onTabChange: (tab: 'buy' | 'sell') => void;
  onOrderTypeChange: (e: any) => void;
  onBuyTabClick: () => void;
  onSellTabClick: () => void;
  onMoreActionSelect?: (action: 'split' | 'merge') => void;
}

const MONO_FONT = 'var(--font-plex-mono), IBM Plex Mono, monospace';
const HEADING_FONT = 'var(--font-archivo), Archivo, sans-serif';

export function TradeTabs({
  activeTab,
  orderType,
  onTabChange,
  onOrderTypeChange,
  onBuyTabClick,
  onSellTabClick,
  onMoreActionSelect,
}: TradeTabsProps) {
  const { t } = useT();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        mb: 2.25,
      }}
    >
      <Box sx={{ display: 'flex', gap: 2.5 }}>
        <Button
          variant="text"
          onClick={onBuyTabClick}
          sx={{
            fontFamily: HEADING_FONT,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.90625rem',
            px: 0,
            py: 0,
            pb: 1.375,
            minWidth: 'auto',
            color: activeTab === 'buy' ? 'text.primary' : 'text.secondary',
            borderBottom: '2px solid',
            borderColor: activeTab === 'buy' ? 'primary.main' : 'transparent',
            borderRadius: 0,
            '&:hover': {
              bgcolor: 'transparent',
              color: 'text.primary',
            },
          }}
        >
          {t('trade.buy', { defaultValue: 'Buy' })}
        </Button>
        <Button
          variant="text"
          onClick={onSellTabClick}
          sx={{
            fontFamily: HEADING_FONT,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.90625rem',
            px: 0,
            py: 0,
            pb: 1.375,
            minWidth: 'auto',
            color: activeTab === 'sell' ? 'text.primary' : 'text.secondary',
            borderBottom: '2px solid',
            borderColor: activeTab === 'sell' ? 'primary.main' : 'transparent',
            borderRadius: 0,
            '&:hover': {
              bgcolor: 'transparent',
              color: 'text.primary',
            },
          }}
        >
          {t('trade.sell', { defaultValue: 'Sell' })}
        </Button>
      </Box>
      <FormControl size="small" sx={{ minWidth: 92, mb: 1.125 }}>
        <Select
          value={orderType === 'more' ? 'market' : orderType}
          onChange={onOrderTypeChange}
          sx={{
            fontFamily: MONO_FONT,
            fontSize: '0.75rem',
            color: 'text.secondary',
            borderRadius: '8px',
            '& .MuiSelect-select': {
              py: 0.75,
              px: 1.375,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider',
            },
          }}
        >
          <MenuItem value="market">{t('trade.market', { defaultValue: 'Market' })}</MenuItem>
          <MenuItem value="limit">{t('trade.limit', { defaultValue: 'Limit' })}</MenuItem>
          <Divider />
          {/* <MenuItem
            value="__merge__"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMoreActionSelect?.('merge');
            }}
          >
            Merge
          </MenuItem>
          <MenuItem
            value="__split__"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMoreActionSelect?.('split');
            }}
          >
            Split
          </MenuItem> */}
        </Select>
      </FormControl>
    </Box>
  );
}
